export interface CockpitStatus {
  unread_incoming: number;
  open_awaiting_action: number;
  open_dispatched: number;
  stale_over_7d: number;
  degraded?: boolean;
}

export interface CockpitObjective {
  title: string;
  details?: string;
  next: string;
}

export interface CockpitNarrative {
  date: string;
  title: string;
  content: string;
}

export interface CockpitNarrativeSummary {
  date: string;
  artifact_id: string;
  title: string;
}

export interface OpenWorkOrder {
  id: string;
  to: string;
  subject: string;
  status: string;
  sent_at: string;
  age_hours: number | null;
}

function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function mutationHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed (${response.status})`);
  }
  return response.json();
}

export async function fetchCockpitStatus(): Promise<CockpitStatus> {
  const res = await fetch('/api/brain/status', { credentials: 'include' });
  return handleResponse<CockpitStatus>(res);
}

export async function fetchCockpitNarrative(date = 'latest'): Promise<CockpitNarrative> {
  const res = await fetch(`/api/brain/narrative?date=${encodeURIComponent(date)}`, { credentials: 'include' });
  return handleResponse<CockpitNarrative>(res);
}

export async function fetchCockpitNarrativeList(): Promise<CockpitNarrativeSummary[]> {
  const res = await fetch('/api/brain/narrative/list', { credentials: 'include' });
  const payload = await handleResponse<{ narratives: CockpitNarrativeSummary[] }>(res);
  return payload.narratives;
}

export async function fetchWorkqueue(): Promise<{ objectives?: CockpitObjective[]; raw?: string }> {
  const res = await fetch('/api/brain/workqueue', { credentials: 'include' });
  return handleResponse<{ objectives?: CockpitObjective[]; raw?: string }>(res);
}

export async function fetchOpenWorkOrders(): Promise<OpenWorkOrder[]> {
  const res = await fetch('/api/brain/work-orders/open', { credentials: 'include' });
  const payload = await handleResponse<{ work_orders: OpenWorkOrder[] }>(res);
  return payload.work_orders;
}

export async function generateCockpitNarrative(): Promise<{
  status: string;
  artifact_id?: string;
  error?: string;
}> {
  const res = await fetch('/api/brain/narrative/generate', {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
  });
  const data = await res.json().catch(() => ({})) as {
    status?: string;
    artifact_id?: string;
    error?: string;
  };
  if (!res.ok) {
    return {
      status: 'error',
      error: data.error || `Request failed (${res.status})`,
    };
  }
  return data as { status: string; artifact_id?: string; error?: string };
}
